// Email one-time-code sign-in (account builds, 0022): email -> 6-digit code -> signed in.
// Only invited people get a code; the server binds the sign-in to the invited account and
// says when it cannot (not invited, invitation expired, suspended, disabled).
import { useQueryClient } from '@tanstack/react-query'
import { useId, useState } from 'react'
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { Banner, Button, Card, Field, TextInput } from '@/components/ui'
import { isAccountBuild } from '@/lib/env'
import { codeProblem, emailProblem, normaliseEmail, safeNext, SIGN_IN_MESSAGES, SignInError, type SignInProblem } from '@/lib/identity'
import { useSession } from '@/lib/queries'
import { requestSignInCode, signInRemainingMs, signOutAccount, verifySignInCode } from '@/lib/session'

function problemOf(e: unknown): SignInProblem {
  return e instanceof SignInError ? e.problem : 'UNKNOWN'
}

/**
 * The two-step form. lockedEmail: signing in again after the hour - only the same person
 * may continue here (anyone else signs out first), so nothing filled in by one person is
 * ever sent under another person's identity.
 */
export function CodeSignInForm({ lockedEmail, onSignedIn }: { lockedEmail?: string; onSignedIn?: () => void }) {
  const queryClient = useQueryClient()
  const ids = { email: useId(), code: useId() }
  const [email, setEmail] = useState(lockedEmail ?? '')
  const [code, setCode] = useState('')
  const [step, setStep] = useState<'email' | 'code'>('email')
  const [busy, setBusy] = useState(false)
  const [fieldError, setFieldError] = useState<string | null>(null)
  const [problem, setProblem] = useState<SignInProblem | null>(null)
  const [sentTo, setSentTo] = useState<string | null>(null)
  // false when the code step is shown without a new email (rate limit, or 'I already have a code').
  const [fresh, setFresh] = useState(true)

  /** The code step for the address typed, keeping any problem banner. */
  function toCode(isFresh: boolean) {
    const p = emailProblem(email)
    setFieldError(p)
    if (p) return
    setSentTo(normaliseEmail(email))
    setFresh(isFresh)
    setStep('code')
    setCode('')
  }

  async function sendCode() {
    const p = emailProblem(email)
    setFieldError(p)
    setProblem(null)
    if (p) return
    setBusy(true)
    try {
      await requestSignInCode(email)
      toCode(true)
    } catch (e) {
      const pr = problemOf(e)
      setProblem(pr)
      // Too many requests: the last code still works, so let them type it.
      if (pr === 'RATE_LIMITED') toCode(false)
    } finally {
      setBusy(false)
    }
  }

  async function verify() {
    const p = codeProblem(code)
    setFieldError(p)
    setProblem(null)
    if (p || !sentTo) return
    setBusy(true)
    try {
      await verifySignInCode(sentTo, code)
      // The same person signing in again keeps the screen (and what they filled in); a new
      // sign-in starts with nothing cached from anyone signed in before.
      if (lockedEmail) void queryClient.invalidateQueries()
      else queryClient.clear()
      onSignedIn?.()
    } catch (e) {
      setProblem(problemOf(e))
    } finally {
      setBusy(false)
    }
  }

  const message = problem ? SIGN_IN_MESSAGES[problem] : null
  return (
    <div data-testid="code-sign-in">
      {message ? (
        <Banner tone={problem === 'RATE_LIMITED' || problem === 'NETWORK' ? 'warn' : 'bad'} title={message.title} className="mb-3">
          <span data-testid="sign-in-problem" data-problem={problem}>{message.body}</span>
        </Banner>
      ) : null}
      {step === 'email' ? (
        <form
          noValidate
          onSubmit={(e) => {
            e.preventDefault()
            void sendCode()
          }}
        >
          <Field label="Your email address" htmlFor={ids.email} error={fieldError} hint="Use the address your account administrator invited.">
            <TextInput
              id={ids.email}
              type="email"
              autoComplete="email"
              inputMode="email"
              value={email}
              readOnly={Boolean(lockedEmail)}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="sign-in-email"
            />
          </Field>
          <div className="flex flex-wrap items-center gap-3">
            <Button type="submit" loading={busy} data-testid="send-code">
              Email me a sign-in code
            </Button>
            <Button type="button" variant="ghost" disabled={busy} onClick={() => toCode(false)} data-testid="have-code">
              I already have a code
            </Button>
          </div>
        </form>
      ) : (
        <form
          noValidate
          onSubmit={(e) => {
            e.preventDefault()
            void verify()
          }}
        >
          <p className="mb-3 text-sm text-ink-soft">
            {fresh ? 'We emailed a 6-digit code to ' : 'Enter the latest 6-digit code emailed to '}
            <strong className="text-ink">{sentTo}</strong>.
          </p>
          <Field label="Sign-in code" htmlFor={ids.code} error={fieldError}>
            <TextInput
              id={ids.code}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={9}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              data-testid="sign-in-code"
            />
          </Field>
          <div className="flex flex-wrap items-center gap-3">
            <Button type="submit" loading={busy} data-testid="verify-code">
              Sign in
            </Button>
            <Button type="button" variant="secondary" disabled={busy} onClick={() => void sendCode()}>
              Send a new code
            </Button>
            {lockedEmail ? null : (
              <Button
                type="button"
                variant="ghost"
                disabled={busy}
                onClick={() => {
                  setStep('email')
                  setProblem(null)
                }}
              >
                Use a different email
              </Button>
            )}
          </div>
        </form>
      )}
    </div>
  )
}

/** /sign-in (account builds). The demo has personas instead. */
export function SignInPage() {
  const session = useSession()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const next = safeNext(params.get('next'))
  if (!isAccountBuild) return <Navigate to="/demo" replace />
  if (session.account && signInRemainingMs() > 0) return <Navigate to={next} replace />
  return (
    <div className="mx-auto max-w-xl" data-testid="sign-in-page">
      <Card>
        <h1 className="display text-[25px] leading-tight font-semibold text-ink">Sign in to Permit to Work</h1>
        <p className="mt-2 mb-4 text-ink-soft">
          Sign in with your own email address. We email you a one-time code; there is no password. Only people invited by an
          account administrator can sign in, and every signature you make is recorded with your own name.
        </p>
        <CodeSignInForm onSignedIn={() => navigate(next, { replace: true })} />
        <p className="mt-4 text-[13px] text-ink-faint">A sign-in lasts one hour. After that you ask for a new code.</p>
      </Card>
    </div>
  )
}

/** After the hour: the same person asks for a new code; anyone else signs out first. */
export function AccountSignInExpired({ email }: { email: string }) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  return (
    <div className="mx-auto max-w-xl" data-testid="sign-in-expired">
      <Card>
        <h1 className="display text-[25px] leading-tight font-semibold text-ink">Your sign-in has ended</h1>
        <p className="mt-2 mb-4 text-ink-soft">
          For safety, a sign-in lasts one hour. Ask for a new code to continue as {email}. Nothing was sent or signed after your
          sign-in ended; a draft keeps what you last saved.
        </p>
        <CodeSignInForm lockedEmail={email} />
        <div className="mt-4 border-t border-line pt-3">
          <Button
            variant="secondary"
            onClick={async () => {
              await signOutAccount()
              queryClient.clear()
              navigate('/sign-in', { replace: true })
            }}
          >
            Not you? Sign out
          </Button>
        </div>
      </Card>
    </div>
  )
}
